import { RenzoWithdrawQueueAbi } from '../abis/renzo'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * Renzo ezETH — per-user indexed enumeration. Deterministic readyAt
 * via `createdAt + coolDownPeriod()`.
 *
 * Three stages:
 *  1. `getOutstandingWithdrawRequests(user)` → count + cooldown
 *     constant in one multicall.
 *  2. `withdrawRequests(user, i)` per index.
 *
 * Note: Renzo deletes entries on claim, compacting the array — the
 * index returned here is the **client-side index at fetch time**.
 * Re-enumerate after each claim.
 */
export const readerRenzo: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getOutstandingWithdrawRequests',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'coolDownPeriod',
          params: [],
        },
      ] as unknown as never[],
      abi: [RenzoWithdrawQueueAbi, RenzoWithdrawQueueAbi] as unknown as never,
    })) as unknown[]
    const count = toNumber(stage1[0]) ?? 0
    const cooldown = toNumber(stage1[1]) ?? 0
    if (count === 0) return []

    const detailCalls = Array.from({ length: count }, (_, i) => ({
      address: entry.withdrawalContract!,
      name: 'withdrawRequests',
      params: [user, BigInt(i)],
    }))
    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: detailCalls as unknown as never[],
      abi: detailCalls.map(() => RenzoWithdrawQueueAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < count; i++) {
      const tup = stage2[i] as unknown[] | undefined
      if (!Array.isArray(tup) || tup.length < 5) continue
      const amountToRedeem = toBigInt(tup[2])
      const createdAt = toNumber(tup[4])
      if (amountToRedeem === undefined || createdAt === undefined) continue
      const readyAt = createdAt + cooldown
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(i),
        amountUnderlying: amountToRedeem.toString(),
        status: computeStatus(readyAt),
        readyAt,
      })
    }
    return out
  },
}
